import {
  Button,
  Dropdown,
  IconButton,
  InlineNotification,
  NumberInput,
  Pagination,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextArea,
  TextInput,
  Toggle,
} from '@carbon/react';
import { Add, Move, TrashCan, Undo } from '@carbon/icons-react';
import {
  asProblem,
  EmptyState,
  FormModal,
  hasPermission,
  isEmptyValue,
  problemMessage,
  StatusTag,
  useAdmin,
  type RecordValues,
} from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { TableLoading } from '../common/Loading';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import { SaveBar } from '../common/SaveBar';
import { AddToMenuDialog } from './AddToMenuDialog';
import {
  addEntries,
  addGroupEntries,
  addGroupTiles,
  addMissingLink,
  addPages,
  addSubPage,
  movePage,
  pageTree,
  parentChoices,
  addTiles,
  changes,
  fromEditor,
  isNewOrChanged,
  offPage,
  PALETTE,
  problems,
  toRequest,
  updateEntry,
  updatePage,
  updateTile,
  type AddKind,
  type DraftPage,
  type MenuDraft,
  groupKey,
  unitKey,
} from './menuDraft';
import '../pricing/pricing.scss';
import './menus.scss';

const SCREENS = ['terminal', 'tablet', 'handheld'] as const;

async function loadEditor(menuId: string, layoutId?: string) {
  const { data, error } = await hoApi.GET('/api/v1/menu/menus/{id}/editor', {
    params: { path: { id: menuId }, query: { layoutId } },
  });
  if (!data) throw error;
  return data;
}

type Adding = { kind: AddKind; pageKey?: string };

/**
 * A menu's items on sale and its layout pages, edited as a whole (FR-MENU-01..05): several pages and several items or
 * deals are added at once, tiles and pages are edited in place, and everything is saved together. Pages and tiles
 * reach stores with the POS profiles that use the layout (ADR 0016); items on sale go with the next sync.
 */
const LOOSE_PAGE_SIZE = 50;
const LOOSE_SMALLEST_PAGE = 25;

export function MenuEditorTab({ menu }: { menu: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.menu.menus.manage');
  const menuId = String(menu.id);
  const queryClient = useQueryClient();
  const [layoutId, setLayoutId] = useState<string>();
  const key = ['ho', 'menu-editor', menuId, layoutId ?? ''];
  const editor = useQuery({ queryKey: key, queryFn: () => loadEditor(menuId, layoutId) });
  const [state, setState] = useState<{ source: unknown; draft: MenuDraft }>();
  const draft = state && state.source === editor.data ? state.draft : editor.data ? fromEditor(editor.data) : undefined;
  const edit = (next: (d: MenuDraft) => MenuDraft) => draft && setState({ source: editor.data, draft: next(draft) });
  const [adding, setAdding] = useState<Adding>();
  const [addingPages, setAddingPages] = useState(false);
  // A page being given a sub-page, or moved under another (ADR 0138).
  const [subPageOf, setSubPageOf] = useState<DraftPage>();
  const [moving, setMoving] = useState<DraftPage>();
  const [notice, setNotice] = useState<string>();
  // A menu can hold thousands of items not on any page: they are shown a page at a time (edits are kept across pages).
  const [loosePaging, setLoosePaging] = useState({ page: 1, pageSize: LOOSE_PAGE_SIZE });
  const [saved, setSaved] = useState<number>();

  const layout = editor.data?.layouts.find((l) => l.id === draft?.layoutId);
  const grid = { rows: Number(layout?.gridRows ?? 0), cols: Number(layout?.gridCols ?? 0) };
  const found = draft ? problems(draft, grid) : new Map();
  const pending = draft ? changes(draft) : 0;

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('nothing to save');
      const { data, error } = await hoApi.PUT('/api/v1/menu/menus/{id}/editor', {
        params: { path: { id: menuId } },
        body: toRequest(draft),
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setSaved(pending);
      setState(undefined);
      queryClient.setQueryData(key, next);
    },
  });

  if (editor.isLoading) return <TableLoading columns={5} label={t('common.loading')} />;
  if (!editor.data || !draft)
    return <ErrorNotice title={problemMessage(t, asProblem(editor.error))} onRetry={() => void editor.refetch()} />;
  const data = editor.data;
  const conflict = (save.error as ApiProblem | null)?.code === 'common.version_conflict';
  const problemText = (k: string) => (found.get(k) ? t(`menuEditor.problems.${found.get(k)}` as I18nKey) : undefined);
  const pages = pageTree(draft);
  const allLoose = offPage(draft);
  const loosePage = Math.min(loosePaging.page, Math.max(1, Math.ceil(allLoose.length / loosePaging.pageSize)));
  const loose = allLoose.slice((loosePage - 1) * loosePaging.pageSize, loosePage * loosePaging.pageSize);

  const addTo = (targets: Parameters<typeof addEntries>[2]) => {
    if (!adding) return;
    const { kind, pageKey } = adding;
    if (!pageKey)
      return edit((d) => (kind === 'group' ? addGroupEntries(d, targets) : addEntries(d, kind, targets)));
    const result =
      kind === 'group' ? addGroupTiles(draft, pageKey, targets, grid) : addTiles(draft, pageKey, kind, targets, grid);
    setState({ source: editor.data, draft: result.draft });
    setNotice(result.left > 0 ? t('menuEditor.noRoom', { count: result.left }) : undefined);
  };

  return (
    <div className="pos-price-table">
      <p className="pos-price-table__hint">{t('menuEditor.hint')}</p>
      {saved !== undefined && (
        <TransientNotice
          kind="success"
          title={t('menuEditor.saved', { count: saved })}
          onDone={() => setSaved(undefined)}
        />
      )}
      {notice && <TransientNotice kind="warning" title={notice} onDone={() => setNotice(undefined)} />}

      {data.layouts.length === 0 ? (
        canEdit ? (
          <CreateLayout menuId={menuId} brandId={data.brandId} onCreated={(id) => setLayoutId(id)} />
        ) : (
          <EmptyState heading={t('menuEditor.noLayoutTitle')} description={t('menuEditor.noLayout')} />
        )
      ) : (
        <>
          <div className="pos-menu-editor__bar">
            <Dropdown
              id="menu-editor-layout"
              titleText={t('menuEditor.layout')}
              label={t('menuEditor.layout')}
              items={data.layouts}
              itemToString={(l) => (l ? `${l.name} · ${t(`md.enums.screen_class.${l.screenClass}` as I18nKey)}` : '')}
              selectedItem={layout ?? null}
              disabled={pending > 0}
              helperText={
                pending > 0 ? t('menuEditor.saveFirst') : t('menuEditor.gridSize', { rows: grid.rows, cols: grid.cols })
              }
              onChange={({ selectedItem }) => selectedItem && setLayoutId(selectedItem.id)}
            />
            {canEdit && (
              <Button kind="tertiary" renderIcon={Add} onClick={() => setAddingPages(true)}>
                {t('menuEditor.addPages')}
              </Button>
            )}
          </div>
          {pages.length === 0 && (
            <EmptyState
              heading={t('menuEditor.noPagesTitle')}
              description={t('menuEditor.noPages')}
            />
          )}
          {pages.map(({ page, depth, parent }) => (
            <PageSection
              key={page.key}
              page={page}
              depth={depth}
              parent={parent}
              canEdit={canEdit}
              problemOf={problemText}
              onPage={(change) => edit((d) => updatePage(d, page.key, change))}
              onTile={(tileKey, change) => edit((d) => updateTile(d, page.key, tileKey, change))}
              onAdd={(kind) => setAdding({ kind, pageKey: page.key })}
              onAddSubPage={() => setSubPageOf(page)}
              onMove={() => setMoving(page)}
              onAddLink={() => edit((d) => addMissingLink(d, page.key))}
              icons={data.pageIcons ?? []}
            />
          ))}
        </>
      )}

      <section className="pos-price-table__group" aria-labelledby="menu-off-page">
        <div className="pos-price-table__group-head">
          <h2 id="menu-off-page" className="pos-price-table__group-title">
            {t('menuEditor.offPage')}{' '}
            <span className="pos-price-table__count">({allLoose.filter((e) => !e.removed).length})</span>
          </h2>
          {canEdit && (
            <div className="pos-menu-editor__actions">
              <Button kind="ghost" renderIcon={Add} onClick={() => setAdding({ kind: 'item' })}>
                {t('menuEditor.add.item')}
              </Button>
              <Button kind="ghost" renderIcon={Add} onClick={() => setAdding({ kind: 'deal' })}>
                {t('menuEditor.add.deal')}
              </Button>
              <Button kind="ghost" renderIcon={Add} onClick={() => setAdding({ kind: 'group' })}>
                {t('menuEditor.add.group')}
              </Button>
            </div>
          )}
        </div>
        <p className="pos-price-table__hint">{t('menuEditor.offPageHint')}</p>
        {loose.length > 0 && (
          <TableContainer>
            <Table aria-labelledby="menu-off-page">
              <TableHead>
                <TableRow>
                  <TableHeader>{t('priceTable.columns.code')}</TableHeader>
                  <TableHeader>{t('priceTable.columns.name')}</TableHeader>
                  <TableHeader>{t('priceTable.columns.unit')}</TableHeader>
                  <TableHeader>{t('menuEditor.columns.visible')}</TableHeader>
                  <TableHeader className="pos-menu-editor__number-col">{t('menuEditor.columns.sort')}</TableHeader>
                  {canEdit && <TableHeader aria-label={t('admin.actions')} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {loose.map((e) => (
                  <TableRow key={e.key} className={rowClass(e.removed, isNewOrChanged.entry(e), !e.entryId)}>
                    <TableCell className="pos-price-table__code">{e.code}</TableCell>
                    <TableCell>
                      <span className="pos-price-table__name">{e.name}</span>
                      <span className="pos-menu-editor__kind">{t(`menuEditor.kinds.${e.kind}` as I18nKey)}</span>
                      {!e.isActive && <StatusTag status="inactive">{t('priceTable.inactive')}</StatusTag>}
                    </TableCell>
                    <TableCell>
                      <UnitText kind={e.kind} code={e.uomCode} />
                    </TableCell>
                    <TableCell>
                      <Toggle
                        id={`visible-${e.key}`}
                        labelText={t('menuEditor.visibleOf', { name: nameOf(e.name, e.uomCode) })}
                        hideLabel
                        labelA={t('menuEditor.hidden')}
                        labelB={t('menuEditor.shown')}
                        toggled={e.isVisible}
                        disabled={!canEdit || e.removed}
                        onToggle={(on) => edit((d) => updateEntry(d, e.key, { isVisible: on }))}
                      />
                    </TableCell>
                    <TableCell className="pos-menu-editor__number-col">
                      <NumberInput
                        id={`sort-${e.key}`}
                        label={t('menuEditor.sortOf', { name: nameOf(e.name, e.uomCode) })}
                        hideLabel
                        hideSteppers
                        min={0}
                        value={e.sortOrder}
                        disabled={!canEdit || e.removed}
                        onChange={(_, { value }) =>
                          edit((d) => updateEntry(d, e.key, { sortOrder: Number(value) || 0 }))
                        }
                      />
                    </TableCell>
                    {canEdit && (
                      <TableCell className="pos-admin-page__actions">
                        <RemoveButton
                          removed={e.removed}
                          name={nameOf(e.name, e.uomCode)}
                          onChange={(removed) => edit((d) => updateEntry(d, e.key, { removed }))}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {allLoose.length > LOOSE_SMALLEST_PAGE && (
          <Pagination
            itemsPerPageText={t('carbon.itemsPerPage')}
            itemRangeText={(min, max, total) => t('carbon.itemRange', { min, max, total })}
            pageRangeText={(_current, total) => t('carbon.pageRange', { total })}
            backwardText={t('carbon.previousPage')}
            forwardText={t('carbon.nextPage')}
            pageNumberText={t('carbon.pageNumber')}
            page={loosePage}
            pageSize={loosePaging.pageSize}
            pageSizes={[25, 50, 100, 200]}
            totalItems={allLoose.length}
            onChange={(next) => setLoosePaging({ page: next.page, pageSize: next.pageSize })}
          />
        )}
      </section>

      {canEdit && (pending > 0 || save.error) && (
        <SaveBar
          summary={t('priceTable.unsaved', { count: pending })}
          problems={[
            ...(found.size > 0 ? [t('menuEditor.fix', { count: found.size })] : []),
            ...(save.error ? [conflict ? t('menuEditor.conflict') : problemMessage(t, asProblem(save.error))] : []),
          ]}
          discardText={conflict ? t('priceTable.reload') : t('priceTable.discard')}
          saveText={t('priceTable.save', { count: pending })}
          saveDisabled={found.size > 0 || pending === 0}
          saving={save.isPending}
          onDiscard={() => {
            save.reset();
            setState(undefined);
            if (conflict) void editor.refetch();
          }}
          onSave={() => save.mutate()}
        />
      )}

      {subPageOf && (
        <AddSubPageDialog
          parent={subPageOf}
          onAdd={(name) => edit((d) => addSubPage(d, subPageOf.key, name))}
          onClose={() => setSubPageOf(undefined)}
        />
      )}
      {moving && (
        <MovePageDialog
          page={moving}
          choices={parentChoices(draft, moving.key).map(({ page, depth }) => ({ key: page.key, name: page.name, depth }))}
          onMove={(parentKey) => edit((d) => movePage(d, moving.key, parentKey))}
          onClose={() => setMoving(undefined)}
        />
      )}
      {addingPages && (
        <AddPagesDialog
          onAdd={(names, paletteKey) => edit((d) => addPages(d, names, paletteKey))}
          onClose={() => setAddingPages(false)}
        />
      )}
      {adding && (
        <AddToMenuDialog
          menuId={menuId}
          kind={adding.kind}
          title={
            adding.pageKey
              ? t(`menuEditor.addTo.${adding.kind}` as I18nKey, {
                  page: draft.pages.find((p) => p.key === adding.pageKey)?.name ?? '',
                })
              : t(`menuEditor.add.${adding.kind}` as I18nKey)
          }
          taken={
            new Set(
              adding.pageKey
                ? (draft.pages.find((p) => p.key === adding.pageKey)?.tiles ?? [])
                    .filter((x) => !x.removed)
                    .map((x) => (x.itemGroupId ? groupKey(x.itemGroupId) : unitKey(x.targetId, x.uomId)))
                : draft.entries.filter((e) => !e.removed).map((e) => unitKey(e.targetId, e.uomId)),
            )
          }
          onAdd={addTo}
          onClose={() => setAdding(undefined)}
        />
      )}
    </div>
  );
}

/** "Bia (CAN)": a row's name with its unit, for assistive technology and messages. */
const nameOf = (name: string, code?: string | null) => (code ? `${name} (${code})` : name);

/** An item's unit on a tile or entry: its code, or "Default unit" when the tile follows the item's default selling unit. */
function UnitText({ kind, code }: { kind: string; code?: string | null }) {
  const { t } = useTranslation();
  if (kind !== 'item') return null;
  return code ? <span className="pos-price-table__code">{code}</span> : <span>{t('menuEditor.defaultUnit')}</span>;
}

function rowClass(removed: boolean, changed: boolean, isNew: boolean) {
  if (removed) return 'pos-price-table__row--removed';
  if (isNew) return 'pos-price-table__row--new';
  return changed ? 'pos-price-table__row--changed' : undefined;
}

function RemoveButton({
  removed,
  name,
  onChange,
}: {
  removed: boolean;
  name: string;
  onChange(removed: boolean): void;
}) {
  const { t } = useTranslation();
  return removed ? (
    <IconButton kind="ghost" label={t('priceTable.undoRemove', { name })} onClick={() => onChange(false)}>
      <Undo />
    </IconButton>
  ) : (
    <IconButton kind="ghost" label={t('priceTable.remove', { name })} onClick={() => onChange(true)}>
      <TrashCan />
    </IconButton>
  );
}

function PaletteSelect({
  id,
  label,
  value,
  allowInherit,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  allowInherit?: boolean;
  disabled?: boolean;
  onChange(value: string): void;
}) {
  const { t } = useTranslation();
  return (
    <Select
      id={id}
      labelText={label}
      hideLabel
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {allowInherit && <SelectItem value="" text={t('menuEditor.samePalette')} />}
      {PALETTE.map((p) => (
        <SelectItem key={p} value={p} text={t(`md.enums.palette_key.${p}` as I18nKey)} />
      ))}
    </Select>
  );
}

/** One layout page: its name, colour and order, and its tiles as a table edited in place. */
function PageSection({
  page,
  depth,
  parent,
  canEdit,
  problemOf,
  onPage,
  onTile,
  onAdd,
  onAddSubPage,
  onMove,
  onAddLink,
  icons,
}: {
  page: DraftPage;
  depth: number;
  parent?: DraftPage;
  canEdit: boolean;
  problemOf(key: string): string | undefined;
  onPage(change: Parameters<typeof updatePage>[2]): void;
  onTile(tileKey: string, change: Parameters<typeof updateTile>[3]): void;
  onAdd(kind: AddKind): void;
  onAddSubPage(): void;
  onMove(): void;
  onAddLink(): void;
  /** The icons a category tab may show on the POS section rail (ADR 0139). */
  icons: readonly string[];
}) {
  const { t } = useTranslation();
  const headingId = `menu-page-${page.key}`;
  const locked = !canEdit || page.removed;
  const tiles = [...page.tiles].sort((a, b) => a.row - b.row || a.col - b.col);
  // A sub-page's tile on its parent is made when the move is saved (ADR 0138).
  const linkOnSave = Boolean(parent) && (!page.pageId || page.placed !== JSON.stringify([page.parentKey, page.relink ?? false]));
  return (
    <section
      className={`pos-price-table__group pos-menu-editor__page${page.removed ? ' pos-menu-editor__page--removed' : ''}${depth > 0 ? ' pos-menu-editor__page--sub' : ''}`}
      style={{ '--pos-menu-depth': Math.min(depth, 4) } as React.CSSProperties}
      aria-labelledby={headingId}
    >
      <div className="pos-menu-editor__page-head">
        <h2 id={headingId} className="pos-price-table__group-title">
          {page.name || t('menuEditor.unnamed')}{' '}
          <span className="pos-price-table__count">({page.tiles.filter((x) => !x.removed).length})</span>
          {parent ? (
            <Tag type="blue" size="sm">
              {t('menuEditor.subPageOf', { parent: parent.name })}
            </Tag>
          ) : (
            <Tag type="cool-gray" size="sm">
              {t('menuEditor.categoryTab')}
            </Tag>
          )}
          {!page.pageId && <StatusTag status="active">{t('priceTable.states.new')}</StatusTag>}
          {page.removed && <StatusTag status="failed">{t('priceTable.states.removed')}</StatusTag>}
        </h2>
        {canEdit && (
          <RemoveButton removed={page.removed} name={page.name} onChange={(removed) => onPage({ removed })} />
        )}
      </div>
      {parent && page.linkMissing && !page.relink && !linkOnSave && !page.removed && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={t('menuEditor.linkMissingTitle')}
          subtitle={t('menuEditor.linkMissingBody', { parent: parent.name })}
        >
          {canEdit && (
            <Button kind="tertiary" size="sm" onClick={onAddLink}>
              {t('menuEditor.addLink', { parent: parent.name })}
            </Button>
          )}
        </InlineNotification>
      )}
      {parent && (linkOnSave || page.relink) && !page.removed && (
        <p className="pos-price-table__hint">{t('menuEditor.linkOnSave', { parent: parent.name })}</p>
      )}
      <div className="pos-menu-editor__page-fields">
        <TextInput
          id={`page-name-${page.key}`}
          labelText={t('menuEditor.pageName')}
          value={page.name}
          disabled={locked}
          invalid={Boolean(problemOf(page.key))}
          invalidText={problemOf(page.key)}
          onChange={(e) => onPage({ name: e.target.value })}
        />
        <Select
          id={`page-palette-${page.key}`}
          labelText={t('menuEditor.palette')}
          value={page.paletteKey}
          disabled={locked}
          onChange={(e) => onPage({ paletteKey: e.target.value })}
        >
          {PALETTE.map((p) => (
            <SelectItem key={p} value={p} text={t(`md.enums.palette_key.${p}` as I18nKey)} />
          ))}
        </Select>
        {!parent && (
          <Select
            id={`page-icon-${page.key}`}
            labelText={t('menuEditor.icon')}
            helperText={t('menuEditor.iconHelp')}
            value={page.icon ?? ''}
            disabled={locked}
            onChange={(e) => onPage({ icon: e.target.value || null })}
          >
            <SelectItem value="" text={t('menuEditor.noIcon')} />
            {icons.map((icon) => (
              <SelectItem key={icon} value={icon} text={t(`menuEditor.icons.${icon}` as I18nKey, { defaultValue: icon })} />
            ))}
          </Select>
        )}
        <NumberInput
          id={`page-sort-${page.key}`}
          label={t('menuEditor.columns.sort')}
          min={0}
          hideSteppers
          value={page.sortOrder}
          disabled={locked}
          onChange={(_, { value }) => onPage({ sortOrder: Number(value) || 0 })}
        />
        {canEdit && !page.removed && (
          <div className="pos-menu-editor__actions">
            <Button kind="tertiary" renderIcon={Add} onClick={() => onAdd('item')}>
              {t('menuEditor.add.item')}
            </Button>
            <Button kind="tertiary" renderIcon={Add} onClick={() => onAdd('deal')}>
              {t('menuEditor.add.deal')}
            </Button>
            <Button kind="tertiary" renderIcon={Add} onClick={() => onAdd('group')}>
              {t('menuEditor.add.group')}
            </Button>
            <Button kind="tertiary" renderIcon={Add} onClick={onAddSubPage}>
              {t('menuEditor.addSubPage')}
            </Button>
            <Button kind="tertiary" renderIcon={Move} onClick={onMove}>
              {t('menuEditor.move')}
            </Button>
          </div>
        )}
      </div>
      {!page.removed &&
        (tiles.length === 0 ? (
          <EmptyState
            headingLevel={3}
            heading={t('menuEditor.noTilesTitle')}
            description={t('menuEditor.noTiles')}
          />
        ) : (
          <TableContainer>
            {/* Fixed column widths, so every page's table lines up with the others whatever its tiles. */}
            <Table aria-labelledby={headingId} className="pos-menu-editor__tiles">
              <TableHead>
                <TableRow>
                  <TableHeader className="pos-menu-editor__code-col">{t('priceTable.columns.code')}</TableHeader>
                  <TableHeader>{t('priceTable.columns.name')}</TableHeader>
                  <TableHeader className="pos-menu-editor__unit-col">{t('priceTable.columns.unit')}</TableHeader>
                  <TableHeader className="pos-menu-editor__label-col">{t('menuEditor.columns.label')}</TableHeader>
                  <TableHeader className="pos-menu-editor__palette-col">{t('menuEditor.palette')}</TableHeader>
                  <TableHeader className="pos-menu-editor__number-col">{t('menuEditor.columns.row')}</TableHeader>
                  <TableHeader className="pos-menu-editor__number-col">{t('menuEditor.columns.col')}</TableHeader>
                  {canEdit && <TableHeader className="pos-menu-editor__actions-col" aria-label={t('admin.actions')} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {tiles.map((tile) => {
                  const off = !canEdit || tile.removed;
                  const problem = problemOf(tile.key);
                  return (
                    <TableRow
                      key={tile.key}
                      className={rowClass(tile.removed, isNewOrChanged.tile(tile), !tile.buttonId)}
                    >
                      <TableCell className="pos-price-table__code">{tile.code}</TableCell>
                      <TableCell>
                        <span className="pos-price-table__name">{tile.name}</span>
                        <span className="pos-menu-editor__kind">
                          {t(`menuEditor.kinds.${tile.targetType}` as I18nKey)}
                        </span>
                        {tile.targetType === 'page' && (
                          <span className="pos-price-table__hint">{t('menuEditor.pageTileHint')}</span>
                        )}
                        {!tile.isActive && <StatusTag status="inactive">{t('priceTable.inactive')}</StatusTag>}
                        {/* An item that is a variant can open its group's variant picker instead (ADR 0052). */}
                        {tile.targetType === 'item' && tile.memberOfGroupId && (
                          <Toggle
                            id={`tile-group-${tile.key}`}
                            size="sm"
                            labelText={t('menuEditor.groupButton', { group: tile.memberOfGroupName ?? '' })}
                            labelA={t('menuEditor.groupButtonOff')}
                            labelB={t('menuEditor.groupButtonOn')}
                            toggled={tile.itemGroupId === tile.memberOfGroupId}
                            disabled={off}
                            onToggle={(on) => onTile(tile.key, { itemGroupId: on ? tile.memberOfGroupId : null })}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <UnitText kind={tile.targetType} code={tile.uomCode} />
                      </TableCell>
                      <TableCell>
                        <TextInput
                          id={`tile-label-${tile.key}`}
                          labelText={t('menuEditor.labelOf', { name: nameOf(tile.name, tile.uomCode) })}
                          hideLabel
                          placeholder={tile.name}
                          value={tile.label}
                          disabled={off}
                          onChange={(e) => onTile(tile.key, { label: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <PaletteSelect
                          id={`tile-palette-${tile.key}`}
                          label={t('menuEditor.paletteOf', { name: nameOf(tile.name, tile.uomCode) })}
                          value={tile.paletteKey}
                          allowInherit
                          disabled={off}
                          onChange={(paletteKey) => onTile(tile.key, { paletteKey })}
                        />
                      </TableCell>
                      {/* Rows and columns count from 1 on screen and from 0 in the layout. */}
                      <TableCell className="pos-menu-editor__number-col">
                        <NumberInput
                          id={`tile-row-${tile.key}`}
                          label={t('menuEditor.rowOf', { name: nameOf(tile.name, tile.uomCode) })}
                          hideLabel
                          min={1}
                          value={tile.row + 1}
                          disabled={off}
                          invalid={Boolean(problem)}
                          invalidText={problem}
                          onChange={(_, { value }) => onTile(tile.key, { row: (Number(value) || 1) - 1 })}
                        />
                      </TableCell>
                      <TableCell className="pos-menu-editor__number-col">
                        <NumberInput
                          id={`tile-col-${tile.key}`}
                          label={t('menuEditor.colOf', { name: nameOf(tile.name, tile.uomCode) })}
                          hideLabel
                          min={1}
                          value={tile.col + 1}
                          disabled={off}
                          invalid={Boolean(problem)}
                          onChange={(_, { value }) => onTile(tile.key, { col: (Number(value) || 1) - 1 })}
                        />
                      </TableCell>
                      {canEdit && (
                        <TableCell className="pos-admin-page__actions">
                          {/* A sub-page's tile goes with the sub-page: move or remove that page instead (ADR 0138). */}
                          {tile.targetType !== 'page' && (
                            <RemoveButton
                              removed={tile.removed}
                              name={nameOf(tile.name, tile.uomCode)}
                              onChange={(removed) => onTile(tile.key, { removed })}
                            />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
    </section>
  );
}

/** A new page under another (ADR 0138): its tile on the parent is made when it is saved. */
function AddSubPageDialog({ parent, onAdd, onClose }: { parent: DraftPage; onAdd(name: string): void; onClose(): void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  return (
    <FormModal
      size="sm"
      title={t('menuEditor.addSubPageTo', { parent: parent.name })}
      primaryText={t('menuEditor.addSubPage')}
      pending={false}
      onSubmit={() => {
        setSubmitted(true);
        if (!name.trim()) return;
        onAdd(name);
        onClose();
      }}
      onClose={onClose}
    >
      <TextInput
        id="menu-sub-page-name"
        labelText={t('menuEditor.pageName')}
        helperText={t('menuEditor.addSubPageHelp', { parent: parent.name })}
        value={name}
        invalid={(touched || submitted) && !name.trim()}
        invalidText={t('admin.validation.required')}
        onBlur={() => setTouched(true)}
        onChange={(e) => setName(e.target.value)}
      />
    </FormModal>
  );
}

const TOP = '';

/** Moves a page under another page, or to the top as a category tab (ADR 0138); never under itself or a page below it. */
function MovePageDialog({
  page,
  choices,
  onMove,
  onClose,
}: {
  page: DraftPage;
  choices: { key: string; name: string; depth: number }[];
  onMove(parentKey: string | null): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState(page.parentKey ?? TOP);
  return (
    <FormModal
      size="sm"
      title={t('menuEditor.moveTitle', { page: page.name })}
      primaryText={t('menuEditor.move')}
      pending={false}
      onSubmit={() => {
        onMove(target === TOP ? null : target);
        onClose();
      }}
      onClose={onClose}
    >
      <Select
        id="menu-page-parent"
        labelText={t('menuEditor.moveUnder')}
        helperText={t('menuEditor.moveHelp')}
        value={target}
        onChange={(e) => setTarget(e.target.value)}
      >
        <SelectItem value={TOP} text={t('menuEditor.categoryTab')} />
        {choices.map((c) => (
          <SelectItem key={c.key} value={c.key} text={`${'— '.repeat(c.depth)}${c.name}`} />
        ))}
      </Select>
    </FormModal>
  );
}

/** Several pages at once: one name per line. */
function AddPagesDialog({ onAdd, onClose }: { onAdd(names: string[], paletteKey: string): void; onClose(): void }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [paletteKey, setPaletteKey] = useState<string>('neutral');
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const names = text
    .split('\n')
    .map((n) => n.trim())
    .filter(Boolean);
  // Both fields are required, so neither is marked (required-marking rule).
  return (
    <FormModal
      size="sm"
      title={t('menuEditor.addPages')}
      primaryText={t('menuEditor.addPagesCount', { count: names.length })}
      pending={false}
      onSubmit={() => {
        setSubmitted(true);
        if (names.length === 0) return;
        onAdd(names, paletteKey);
        onClose();
      }}
      onClose={onClose}
    >
      <TextArea
        id="menu-page-names"
        labelText={t('menuEditor.pageNames')}
        helperText={t('menuEditor.pageNamesHelp')}
        required
        rows={6}
        value={text}
        invalid={(touched || submitted) && isEmptyValue(names)}
        invalidText={t('admin.validation.required')}
        onBlur={() => setTouched(true)}
        onChange={(e) => setText(e.target.value)}
      />
      <Select
        id="menu-page-palette"
        labelText={t('menuEditor.palette')}
        required
        value={paletteKey}
        onChange={(e) => setPaletteKey(e.target.value)}
      >
        {PALETTE.map((p) => (
          <SelectItem key={p} value={p} text={t(`md.enums.palette_key.${p}` as I18nKey)} />
        ))}
      </Select>
    </FormModal>
  );
}

/** A menu with no layout yet gets one here, so its pages can be set up straight away. */
function CreateLayout({
  menuId,
  brandId,
  onCreated,
}: {
  menuId: string;
  brandId: string;
  onCreated(id: string): void;
}) {
  const { t } = useTranslation();
  const [screenClass, setScreenClass] = useState<string>('terminal');
  const [rows, setRows] = useState(5);
  const [cols, setCols] = useState(6);
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/menu/layouts', {
        body: {
          brandId,
          menuId,
          code: screenClass.toUpperCase(),
          name: t(`md.enums.screen_class.${screenClass}` as I18nKey),
          screenClass,
          gridRows: rows,
          gridCols: cols,
        },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (layout) => {
      void queryClient.invalidateQueries({ queryKey: ['ho', 'menu-editor', menuId] });
      onCreated(layout.id);
    },
  });
  return (
    <section className="pos-menu-editor__create" aria-labelledby="menu-create-layout">
      <h2 id="menu-create-layout" className="pos-price-table__group-title">
        {t('menuEditor.createLayout')}
      </h2>
      <p className="pos-price-table__hint">{t('menuEditor.noLayout')}</p>
      <div className="pos-menu-editor__page-fields">
        <Select
          id="layout-screen"
          labelText={t('md.fields.screen_class')}
          value={screenClass}
          onChange={(e) => setScreenClass(e.target.value)}
        >
          {SCREENS.map((s) => (
            <SelectItem key={s} value={s} text={t(`md.enums.screen_class.${s}` as I18nKey)} />
          ))}
        </Select>
        <NumberInput
          id="layout-rows"
          label={t('menuEditor.gridRows')}
          min={1}
          max={20}
          value={rows}
          onChange={(_, { value }) => setRows(Number(value) || 1)}
        />
        <NumberInput
          id="layout-cols"
          label={t('menuEditor.gridCols')}
          min={1}
          max={20}
          value={cols}
          onChange={(_, { value }) => setCols(Number(value) || 1)}
        />
        <div className="pos-menu-editor__actions">
          <Button kind="primary" disabled={create.isPending} onClick={() => create.mutate()}>
            {t('menuEditor.createLayout')}
          </Button>
        </div>
      </div>
      {create.error && <ErrorNotice title={problemMessage(t, asProblem(create.error))} />}
    </section>
  );
}
